import { Suspense, lazy } from "react";
import type { LegacyRouteSlug } from "../hooks/useLegacyRouteResolver";
import { LegacyPageSkeleton } from "./LegacyPageSkeleton";

// Lazy load legacy page components for code splitting
const LegacyDocumentsPage = lazy(() =>
  import("../pages/legacy/LegacyDocumentsPage").then((m) => ({
    default: m.LegacyDocumentsPage,
  }))
);

const LegacyEventsPage = lazy(() =>
  import("../pages/legacy/LegacyEventsPage").then((m) => ({
    default: m.LegacyEventsPage,
  }))
);

const LegacyContentPage = lazy(() =>
  import("../pages/legacy/LegacyContentPage").then((m) => ({
    default: m.LegacyContentPage,
  }))
);

const LegacyBookingPage = lazy(() =>
  import("../pages/legacy/LegacyBookingPage").then((m) => ({
    default: m.LegacyBookingPage,
  }))
);

const LegacyCommunicationsPage = lazy(() =>
  import("../pages/legacy/LegacyCommunicationsPage").then((m) => ({
    default: m.LegacyCommunicationsPage,
  }))
);

interface LegacyPageRendererProps {
  slug: LegacyRouteSlug;
}

/**
 * Renders the appropriate legacy page component based on the slug.
 *
 * Uses React.lazy for code splitting so legacy page bundles are only
 * loaded when needed.
 */
export const LegacyPageRenderer = ({ slug }: LegacyPageRendererProps) => {
  const LegacyComponent = getLegacyComponent(slug);

  return (
    <Suspense fallback={<LegacyPageSkeleton />}>
      <LegacyComponent />
    </Suspense>
  );
};

/**
 * Maps legacy route slugs to their corresponding lazy-loaded components.
 */
const getLegacyComponent = (slug: LegacyRouteSlug) => {
  switch (slug) {
    case "documents":
      return LegacyDocumentsPage;
    case "events":
      return LegacyEventsPage;
    case "content":
      return LegacyContentPage;
    case "book-an-appointment":
      return LegacyBookingPage;
    case "communications":
      return LegacyCommunicationsPage;
  }
};
